// Adapter for the reschedule calendar API (api/v2/packages/get-reschedule-calendar): a lean
// two-phase calendar (day list, then per-day slots) mapped onto the Worker[] /
// DatePackageAvailable[] / SPAvaialble[] shapes the reschedule modal consumes.

import moment from 'moment'
import { ticketApi } from './TicketApi'
import { computeCalendarLimitDays, mapCalendarDaysToLegacy } from './PackageAvailabilityV2'
import {
  DatePackageAvailable,
  PackageAvailableCalendarResponse,
  RouteOptimizationCfg,
  SPAvaialble,
  Worker,
} from '../models/OrderDrawerModel'
import { RescheduleTicketRequest } from '../models/RescheduleTicket'
import {
  normalizeRouteOptimizationCfg,
  pickRouteOptimizationCfg,
} from 'app/utils/helpers/OrderDrawerHelpers'
import { DEFAULT_WORKER } from 'app/utils/common/constants'

const MS_PER_DAY = 86_400_000

const startOfLocalDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate())

// Day-list request: the legacy "Start Date" filter maps to startDay (days from today), since on
// V2 a selectedDateString would trigger the slot phase instead. The default window is sized to
// the visible month grid (same as Create Order); month navigation pages further via `startDay`.
export const buildRescheduleDayRequest = (
  base: Pick<RescheduleTicketRequest, 'ticketId' | 'clientTimeOffset' | 'shiftType' | 'selectWorkerId'>,
  startDate?: Date | null,
  limitDays = computeCalendarLimitDays(new Date(), new Date()),
  startDay?: number
): RescheduleTicketRequest => {
  const req: RescheduleTicketRequest = { ...base, limitDays }
  if (startDay !== undefined && startDay > 0) {
    req.startDay = startDay
  } else if (startDate) {
    const diff = Math.ceil(
      (startOfLocalDay(new Date(startDate)).getTime() - startOfLocalDay(new Date()).getTime()) / MS_PER_DAY
    )
    if (diff > 0) req.startDay = diff
  }
  return req
}

// Days-from-today (inclusive) a day-list request reaches; feeds calendarLoadedUntilDay bookkeeping.
export const requestDayReach = (req: RescheduleTicketRequest): number =>
  (req.startDay ?? 0) + (req.limitDays ?? 0)

export const fetchRescheduleCalendarDays = async (
  req: RescheduleTicketRequest
): Promise<{ calendar: PackageAvailableCalendarResponse | null; days: DatePackageAvailable[] }> => {
  const { data } = await ticketApi.getRescheduleCalendar(req)
  const calendar = data ?? null
  return { calendar, days: mapCalendarDaysToLegacy(calendar) }
}

const toDateOrUndefined = (value?: string | null): Date | undefined => {
  if (!value) return undefined
  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? undefined : parsed
}

// Per-worker first-available dates, keyed by upper-cased workerId.
export const buildWorkerStartDateMap = (
  calendar: PackageAvailableCalendarResponse | null
): Map<string, Date> => {
  const map = new Map<string, Date>()
  ;(calendar?.workerStartDates || []).forEach((w) => {
    const date = toDateOrUndefined(w.firstAvailableDate)
    if (w.workerId && date) map.set(w.workerId.toUpperCase(), date)
  })
  return map
}

// The `workers` array the modal consumes: a Based-on-Availability entry holding the pooled day
// list, plus one entry per qualified worker (day calendar empty — filled on selection via a
// selectWorkerId refetch).
export const buildRescheduleWorkers = (
  calendar: PackageAvailableCalendarResponse | null,
  pooledDays: DatePackageAvailable[],
  labels: { baseOnAvailability: string }
): Worker[] => {
  if (!calendar) return []

  const startDateByWorkerId = buildWorkerStartDateMap(calendar)
  const preSelect = calendar.preSelectProvider

  const baseOn: Worker = {
    workerId: DEFAULT_WORKER,
    spId: preSelect?.spId,
    workerNameAr: labels.baseOnAvailability,
    workerNameEn: labels.baseOnAvailability,
    startDate: undefined, // derived from the pooled day list downstream
    isAuto: true,
    isBaseOnAvailable: true,
    personalImageUrl: '/assets/images/common/unknown-worker.png',
    listAvailableDay: pooledDays,
    visitReminder: preSelect?.visitReminder ?? '',
    routeOptimizationCfg: pickRouteOptimizationCfg(preSelect),
  }

  const workers: Worker[] = (calendar.workers || []).map((w) => ({
    workerId: w.workerId,
    spId: w.spId,
    workerNameAr: w.workerNameAr,
    workerNameEn: w.workerNameEn,
    startDate: startDateByWorkerId.get(w.workerId?.toUpperCase() ?? ''),
    isBaseOnAvailable: false,
    isFavoriteWorker: w.isFav,
    isLastCompleted: w.isLastCompleted ?? false,
    personalImageUrl: w.personalImageUrl,
    listAvailableDay: [],
    visitReminder: w.visitReminder ?? undefined,
    routeOptimizationCfg: pickRouteOptimizationCfg(w),
  }))

  return [baseOn, ...workers]
}

export interface RescheduleSlotResult {
  calendar: PackageAvailableCalendarResponse | null
  availableDay?: DatePackageAvailable
  visitReminder: string
}

// Slot phase: the selected day's slots, with one provider entry per bus.
export const fetchRescheduleSlots = async (
  req: RescheduleTicketRequest,
  date: Date
): Promise<RescheduleSlotResult> => {
  const { data } = await ticketApi.getRescheduleCalendar(req)
  const calendar = data ?? null
  const days = mapCalendarDaysToLegacy(calendar)
  const availableDay = days.find((d) => moment(d.dateCheck).isSame(date, 'day'))
  return {
    calendar,
    availableDay,
    visitReminder: calendar?.preSelectProvider?.visitReminder ?? '',
  }
}

// routeOptimizationCfg + visitReminder for a selected bus: the matching provider entry is
// authoritative; preselect/ticket fallbacks apply only when no entry matches.
export const resolveProviderSlotContext = (
  calendar: PackageAvailableCalendarResponse | null | undefined,
  spId: string | undefined,
  fallback?: {
    routeOptimizationCfg?: RouteOptimizationCfg | null
    visitReminder?: string
    ticketSpId?: string
    ticketCfg?: unknown
  }
): { routeOptimizationCfg: RouteOptimizationCfg | null; visitReminder: string } => {
  const spKey = spId?.toUpperCase()
  const slotDay = calendar?.days?.find((d) => d.providers && d.providers.length > 0)
  const provider = spKey
    ? slotDay?.providers?.find((p) => p.spId?.toUpperCase() === spKey)
    : undefined

  if (provider) {
    return {
      routeOptimizationCfg: normalizeRouteOptimizationCfg(provider.routeOptimizationCfg),
      visitReminder: provider.visitReminder ?? fallback?.visitReminder ?? '',
    }
  }

  const preSelect = calendar?.preSelectProvider
  const cfg =
    (spKey && preSelect?.spId?.toUpperCase() === spKey ? pickRouteOptimizationCfg(preSelect) : null) ??
    (spKey && fallback?.ticketSpId?.toUpperCase() === spKey
      ? normalizeRouteOptimizationCfg(fallback?.ticketCfg)
      : null) ??
    fallback?.routeOptimizationCfg ??
    null

  return {
    routeOptimizationCfg: cfg,
    visitReminder: preSelect?.visitReminder ?? fallback?.visitReminder ?? '',
  }
}

// Response object for ServiceProvider.fetchData so its worker pipeline runs unchanged.
export const buildRescheduleData = (
  calendar: PackageAvailableCalendarResponse | null,
  pooledDays: DatePackageAvailable[],
  ticketNumberOfWorker: number | undefined,
  labels: { baseOnAvailability: string }
): {
  workers: Worker[]
  numberOfWorker: number
  isShowDeliveryTime: boolean
  visitReminder: string
} => ({
  workers: buildRescheduleWorkers(calendar, pooledDays, labels),
  numberOfWorker: ticketNumberOfWorker || 1,
  isShowDeliveryTime: calendar?.isShowDeliveryTime ?? false,
  visitReminder: calendar?.preSelectProvider?.visitReminder ?? '',
})
